import { useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { crosswordGridSize } from '../contentTypes'
import type { ChoiceContent, CrosswordContent, ListeningContent, TrueFalseContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'
import { ChoiceRenderer, TrueFalseRenderer } from './ChoiceRenderers'

/**
 * Рендереры медийных типов: сетка кроссворда/филворда и аудирование с
 * серверным лимитом прослушиваний.
 */

/** Ответ кроссворда: `{ cells: { "row:col": "A" } }`; филворда: `{ found: [слова] }` */
export function CrosswordRenderer({ content, answers, onChange, readOnly }: RendererProps<CrosswordContent>) {
  const { t } = useTranslation()

  if (content.variant === 'wordsearch') {
    return <WordsearchRenderer content={content} answers={answers} onChange={onChange} readOnly={readOnly} />
  }

  const cells = typeof answers.cells === 'object' && answers.cells !== null
    ? (answers.cells as Record<string, string>)
    : {}

  const grid = content.grid
  // Размеры читаем через хелпер: сервер кладёт их и плоско, и в grid.size —
  // прямое чтение grid.cols давало repeat(undefined, 32px) и пустую сетку
  const { rows, cols } = crosswordGridSize(grid)
  const placements = grid?.placements ?? []

  if (!grid || rows === 0 || cols === 0) return <Alert severity="warning">{t('exercises.run.gridMissing')}</Alert>

  // Клетки сетки, доступные для ввода: только те, что заняты словами
  const editable = new Map<string, number>()
  placements.forEach((placement, placementIndex) => {
    placement.cells.forEach((cell) => editable.set(`${cell.row}:${cell.col}`, placementIndex))
  })

  return (
    <Stack spacing={3}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 32px)`,
            gap: '2px',
            width: 'max-content',
          }}
        >
          {Array.from({ length: rows * cols }, (_, index) => {
            const row = Math.floor(index / cols)
            const col = index % cols
            const key = `${row}:${col}`
            const isEditable = editable.has(key)

            if (!isEditable) return <Box key={key} sx={{ width: 32, height: 32, bgcolor: 'action.hover' }} />

            return (
              <Box
                key={key}
                component="input"
                maxLength={1}
                value={cells[key] ?? ''}
                disabled={readOnly}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({ ...answers, cells: { ...cells, [key]: event.target.value.toUpperCase().slice(0, 1) } })
                }
                sx={{
                  width: 32,
                  height: 32,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  font: 'inherit',
                }}
              />
            )
          })}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.clues')}
        </Typography>
        <Stack spacing={0.5}>
          {placements.map((placement, index) => (
            <Typography key={index} variant="body2">
              {placement.number ?? index + 1}.{' '}
              {t(placement.direction === 'across' ? 'exercises.run.across' : 'exercises.run.down')} —{' '}
              {placement.clue || content.words[index]?.clue || ''}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

function WordsearchRenderer({ content, answers, onChange, readOnly }: RendererProps<CrosswordContent>) {
  const { t } = useTranslation()
  const found = Array.isArray(answers.found) ? (answers.found as string[]) : []
  const [typed, setTyped] = useState('')
  const letters = content.grid?.letters ?? []

  // Без букв поле пустое: молчаливая пустота выглядела как «задание сломалось»
  if (letters.length === 0) return <Alert severity="warning">{t('exercises.run.gridMissing')}</Alert>

  return (
    <Stack spacing={3}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'inline-block', fontFamily: 'monospace', fontSize: 20, letterSpacing: 6 }}>
          {letters.map((row, rowIndex) => (
            <Box key={rowIndex}>{row.join(' ')}</Box>
          ))}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="input"
          value={typed}
          disabled={readOnly}
          placeholder={t('exercises.run.foundWord')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTyped(event.target.value.toUpperCase())}
          sx={{
            p: 1,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'transparent',
            color: 'text.primary',
            font: 'inherit',
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={readOnly || typed.trim().length < 2}
          onClick={() => {
            onChange({ ...answers, found: [...found, typed.trim()] })
            setTyped('')
          }}
        >
          {t('exercises.run.addFound')}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {found.map((word, index) => (
          <Chip
            key={`${word}-${index}`}
            label={word}
            disabled={readOnly}
            onDelete={readOnly ? undefined : () => onChange({ ...answers, found: found.filter((_, i) => i !== index) })}
          />
        ))}
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.clues')}
        </Typography>
        <Stack spacing={0.5}>
          {content.words.map((word, index) => (
            <Typography key={index} variant="body2">
              {index + 1}. {word.clue}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

/** Ответ аудирования: `{ questions: [ответ вложенного вопроса, …] }` */
export function ListeningRenderer({ content, answers, onChange, readOnly }: RendererProps<ListeningContent>) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [plays, setPlays] = useState(0)
  const maxPlays = content.max_plays ?? null
  const playsLeft = maxPlays === null ? null : Math.max(maxPlays - plays, 0)

  const questionAnswers = useMemo(
    () => (Array.isArray(answers.questions) ? (answers.questions as Array<Record<string, unknown>>) : []),
    [answers.questions],
  )

  const setQuestionAnswer = (index: number, value: Record<string, unknown>) => {
    const next = content.questions.map((_, i) => (i === index ? value : questionAnswers[i] ?? {}))
    onChange({ ...answers, questions: next })
  }

  const audioUrl = content.audio?.url ?? content.audio?.path ?? null

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={readOnly || audioUrl === null || playsLeft === 0}
          onClick={() => {
            setPlays((value) => value + 1)
            void audioRef.current?.play()
          }}
        >
          {t('exercises.run.play')}
        </Button>
        {playsLeft !== null && (
          <Typography variant="body2" color="text.secondary">
            {t('exercises.run.playsLeft', { count: playsLeft })}
          </Typography>
        )}
      </Stack>

      {audioUrl && <Box component="audio" ref={audioRef} src={audioUrl} sx={{ display: 'none' }} />}
      {audioUrl === null && <Alert severity="warning">{t('exercises.run.audioMissing')}</Alert>}

      <Divider />

      <Stack spacing={3}>
        {content.questions.map((question, index) => (
          <Box key={index}>
            {question.type === 'choice' ? (
              <ChoiceRenderer
                content={question.content as unknown as ChoiceContent}
                answers={questionAnswers[index] ?? {}}
                onChange={(value) => setQuestionAnswer(index, value)}
                readOnly={readOnly}
              />
            ) : (
              <TrueFalseRenderer
                content={question.content as unknown as TrueFalseContent}
                answers={questionAnswers[index] ?? {}}
                onChange={(value) => setQuestionAnswer(index, value)}
                readOnly={readOnly}
              />
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
